import { useContext, useEffect, useState } from 'react';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { ContentContext } from '../Context/ContentContext';
import { ApplicationContext } from '../../../../contexts/ApplicationContext';
import { installmentRateFor } from '../../../../utils/chargeRules';

type Installment = { number: number; price: number; label?: string };

/**
 * Juros arredondados para CIMA no centavo — a conta do pay — sem deixar o ruído
 * de ponto flutuante virar 1 centavo a mais: R$ 1.200 em 12x somava
 * 233,22000000000003 e a tela mostrava R$ 233,23, cobrando-se R$ 233,22.
 */
export const roundTaxesUp = (taxes: number) =>
	Math.ceil(Number((taxes * 100).toFixed(6))) / 100;

export const useInstallmentTax = () => {
	const { content } = useContext(ContentContext);
	const { finance } = useContext(ApplicationContext);

	const [allInstallments, setAllInstallments] = useState<Installment[]>([]);
	const [installments, setInstallments] = useState<Installment[]>([]);
	const [discount, setDiscount] = useState(0);

	/**
	 * `cashPrice`: valor da 1x quando ela tem preço próprio — o desconto à vista do plano, já com
	 * a regra do pay (ciclo elegível, cupom depois do desconto). Ver `planChargeBreakdown`.
	 *
	 * Antes o hook tirava `plan.discountOnPaymentInCash` da 1x por conta própria: em qualquer
	 * ciclo (o pay só aplica de bimestral a anual) e DEPOIS do cupom (o pay aplica antes).
	 */
	const calculate = (
		price: number,
		maxInstallment: number,
		hasTaxes: boolean,
		discountToBeSet = 0,
		cashPrice?: number
	) => {
		let discountToBeApplied = 0;

		if (content && content?.pricing.isSmartInstallmentEnabled) {
			discountToBeApplied = discountToBeSet;
		}

		if (content && content?.pricing.isSmartInstallmentEnabled) {
			setDiscount(discountToBeSet);
		}

		const newInstallments = [];

		for (let i = 1; i <= (maxInstallment || 12); i++) {
			const installmentValue = (price || 0) / i;
			let taxes = 0;

			if (hasTaxes) {
				// A taxa é a da plataforma — a mesma que o pay cobra. Era 2,99% fixo.
				const rate = installmentRateFor(finance?.installmentRates, i);
				for (let j = 1; j <= i; j++) {
					taxes += i === 1 ? 0 : installmentValue * j * rate;
				}
			}

			const installment = installmentValue + roundTaxesUp(taxes) / i;

			newInstallments.push({
				number: i,
				price:
					i === 1
						? cashPrice !== undefined
							? Number(cashPrice.toFixed(2))
							: Number(
									(installment - discountToBeApplied).toFixed(
										2
									)
							  )
						: installment
			});
		}

		setAllInstallments(newInstallments);
	};

	useEffect(() => {
		const installmentsSchema =
			content?.pricing.pricingSchema?.installmentsSchema;

		if (!installmentsSchema || installmentsSchema?.length === 0) {
			setInstallments(allInstallments);
			return;
		}

		const result = allInstallments.filter((installment) => {
			return installmentsSchema.find(
				(schema) => schema.count === installment.number
			)?.isActive;
		});

		setInstallments(
			result.map((row) => {
				const firstInstallment = installmentsSchema.find(
					(schema) => schema.count === 1
				);
				const secondInstallment = installmentsSchema.find(
					(schema) => schema.count > 1
				);

				return {
					number: row.number,
					price: row.price,
					label: `1x de ${formatCurrency(
						(firstInstallment?.price || 0) - discount
					)} + ${row.number - 1}x de ${formatCurrency(
						secondInstallment?.price || 0
					)}`
				};
			})
		);
	}, [
		allInstallments,
		content?.pricing.pricingSchema?.installmentsSchema,
		discount
	]);

	return {
		installments,
		calculate
	};
};
